/**
 * SeatsCard — seats included in the deal (paidTherapistsCount + seatsNote)
 * with the live active-therapist count and a mismatch warning. Own save.
 */

import { useEffect, useState } from "react";
import { FloppyDisk, UsersThree, WarningCircle } from "@phosphor-icons/react";
import { useToast } from "../../../../../contexts/ToastContext";
import { useUpdateNivClinicCrmMutation } from "../../../../../store/services/nivApi";
import { getSeatMismatch } from "../nivDerived";
import { CardHead } from "./paymentUi";
import "../ClinicPaymentPanel.css";

const crmToForm = (crm) => ({
  paidTherapistsCount: crm?.paidTherapistsCount ?? "",
  seatsNote: crm?.seatsNote || "",
});

function SeatsCard({ clinic, crm }) {
  const { showSuccess, showError } = useToast();
  const clinicId = clinic?.clinicId;
  const [updateCrm, { isLoading }] = useUpdateNivClinicCrmMutation();
  const [form, setForm] = useState(() => crmToForm(crm));

  useEffect(() => {
    setForm(crmToForm(crm));
  }, [clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!clinic) return null;

  const active = clinic.activeTherapistsCount ?? clinic.therapistsCount ?? 0;
  const mismatch = getSeatMismatch({ crm, activeTherapistsCount: clinic.activeTherapistsCount, therapistsCount: clinic.therapistsCount });

  const handleSave = async () => {
    try {
      await updateCrm({
        clinicId,
        updates: {
          paidTherapistsCount: form.paidTherapistsCount === "" ? null : Number(form.paidTherapistsCount),
          seatsNote: form.seatsNote.trim() || null,
        },
      }).unwrap();
      showSuccess("המושבים נשמרו");
    } catch (error) {
      console.error("[CK-9816] Error saving clinic seats:", {
        errorCode: "CK-9816",
        errorMessage: error?.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת המושבים");
    }
  };

  return (
    <section className="ncpp-card">
      <CardHead icon={UsersThree} eyebrow="ניהול לקוח" title="מושבים">
        <span className="ncpp-planend">פעילים כיום: {active}</span>
      </CardHead>

      {mismatch && (
        <div className="ncpp-banner ncpp-banner-warning" role="status">
          <WarningCircle size={15} weight="fill" />
          <span>
            {mismatch.active} מטפלים פעילים אך התשלום כולל {mismatch.paid} — פער של {mismatch.excess} מושבים
          </span>
        </div>
      )}

      <div className="ncpp-grid-2">
        <label className="ncpp-field">
          <span className="ncpp-field-label">מטפלים כלולים בתשלום</span>
          <input
            type="number"
            min="0"
            inputMode="numeric"
            value={form.paidTherapistsCount}
            onChange={(e) => setForm((f) => ({ ...f, paidTherapistsCount: e.target.value }))}
            placeholder="ללא מעקב"
          />
        </label>
        <label className="ncpp-field">
          <span className="ncpp-field-label">הערת מושבים</span>
          <input
            type="text"
            value={form.seatsNote}
            onChange={(e) => setForm((f) => ({ ...f, seatsNote: e.target.value }))}
            placeholder="לדוגמה: מטפלת שנייה בחינם עד ינואר"
          />
        </label>
      </div>

      <button type="button" className="ncpp-btn ncpp-btn-primary" onClick={handleSave} disabled={isLoading}>
        <FloppyDisk size={16} />
        {isLoading ? "שומר…" : "שמור מושבים"}
      </button>
    </section>
  );
}

export default SeatsCard;
